import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Box,
  HStack,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
  Progress,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';

const AnonymousSignupModal = ({ isOpen, onClose, featureName }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { convertAnonymousToRegistered } = useAuth();

  // Password strength calculation effect
  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(formData.password);
    
    // Length check (8+ characters)
    if (formData.password.length >= 8) strength += 20;
    
    // Uppercase check
    if (/[A-Z]/.test(formData.password)) strength += 20;
    
    // Lowercase check
    if (/[a-z]/.test(formData.password)) strength += 20;
    
    // Number check
    if (/[0-9]/.test(formData.password)) strength += 20;
    
    // Special character check (bonus 20%)
    if (hasSpecialChar) strength += 20;
    
    setPasswordStrength(strength);
  }, [formData.password]);
  
  // Get color based on password strength
  const getStrengthColor = () => {
    if (passwordStrength < 60) return "red.500";
    if (passwordStrength <= 80) return "yellow.500";
    return "green.500";
  };
  
  // Get label based on password strength
  const getStrengthLabel = () => {
    if (passwordStrength < 60) return "Poor";
    if (passwordStrength <= 80) return "Fair";
    return "Strong";
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError('All fields are required');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Password validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    
    if (!/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Convert anonymous user to registered
      const result = await convertAnonymousToRegistered(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );
      
      if (result.success) {
        // Close modal - user can now manually retry their action
        // User profile will be automatically refetched by auth context
        onClose();
      } else {
        setError(result.message);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error during signup:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="#1a1a22" color="white" borderColor="gray.600" borderWidth={1}>
        <ModalHeader textAlign="center">
          Sign Up Now!
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error" bg="red.900" color="white" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              <VStack spacing={3}>
                <HStack spacing={3} width="100%">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">First Name</FormLabel>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      bg="gray.800"
                      borderColor="gray.600"
                      _hover={{ borderColor: "gray.500" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.6)" }}
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Last Name</FormLabel>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      bg="gray.800"
                      borderColor="gray.600"
                      _hover={{ borderColor: "gray.500" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.6)" }}
                    />
                  </FormControl>
                </HStack>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    bg="gray.800"
                    borderColor="gray.600"
                    _hover={{ borderColor: "gray.500" }}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.6)" }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Password</FormLabel>
                  <InputGroup>
                    <Input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      bg="gray.800"
                      borderColor="gray.600"
                      _hover={{ borderColor: "gray.500" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.6)" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        onClick={() => setShowPassword(!showPassword)}
                        _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {formData.password && (
                    <Box mt={2}>
                      <Flex align="center" justify="space-between" mb={1}>
                        <Text fontSize="xs" color={getStrengthColor()}>
                          Strength: {getStrengthLabel()}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {passwordStrength}%
                        </Text>
                      </Flex>
                      <Progress value={passwordStrength} colorScheme={getStrengthColor().split('.')[0]} size="sm" borderRadius="md" />
                      
                      <Box mt={2} fontSize="xs">
                        <List spacing={0.5}>
                          <ListItem display="flex" alignItems="center">
                            <ListIcon as={formData.password.length >= 8 ? CheckIcon : CloseIcon} 
                              color={formData.password.length >= 8 ? "green.500" : "red.500"} 
                              boxSize={3} />
                            <Text color={formData.password.length >= 8 ? "green.400" : "gray.500"}>
                              8+ characters
                            </Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="center">
                            <ListIcon as={/[A-Z]/.test(formData.password) ? CheckIcon : CloseIcon} 
                              color={/[A-Z]/.test(formData.password) ? "green.500" : "red.500"} 
                              boxSize={3} />
                            <Text color={/[A-Z]/.test(formData.password) ? "green.400" : "gray.500"}>
                              Uppercase letter
                            </Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="center">
                            <ListIcon as={/[a-z]/.test(formData.password) ? CheckIcon : CloseIcon} 
                              color={/[a-z]/.test(formData.password) ? "green.500" : "red.500"} 
                              boxSize={3} />
                            <Text color={/[a-z]/.test(formData.password) ? "green.400" : "gray.500"}>
                              Lowercase letter
                            </Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="center">
                            <ListIcon as={/[0-9]/.test(formData.password) ? CheckIcon : CloseIcon} 
                              color={/[0-9]/.test(formData.password) ? "green.500" : "red.500"} 
                              boxSize={3} />
                            <Text color={/[0-9]/.test(formData.password) ? "green.400" : "gray.500"}>
                              Number
                            </Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="center">
                            <ListIcon as={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(formData.password) ? CheckIcon : CloseIcon} 
                              color={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(formData.password) ? "green.500" : "red.500"} 
                              boxSize={3} />
                            <Text color={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(formData.password) ? "green.400" : "gray.500"}>
                              Special character
                            </Text>
                          </ListItem>
                          {formData.confirmPassword && (
                            <ListItem display="flex" alignItems="center">
                              <ListIcon as={formData.password === formData.confirmPassword && formData.confirmPassword ? CheckIcon : CloseIcon} 
                                color={formData.password === formData.confirmPassword && formData.confirmPassword ? "green.500" : "red.500"} 
                                boxSize={3} />
                              <Text color={formData.password === formData.confirmPassword && formData.confirmPassword ? "green.400" : "gray.500"}>
                                Passwords match
                              </Text>
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    </Box>
                  )}
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      bg="gray.800"
                      borderColor="gray.600"
                      _hover={{ borderColor: "gray.500" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.6)" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              </VStack>
            </form>
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            mr={3}
            color="gray.300"
            _hover={{ bg: "gray.700" }}
          >
            Cancel
          </Button>
          <Button
            bg="rgba(65, 105, 225, 0.8)"
            color="white"
            _hover={{ bg: "rgba(65, 105, 225, 0.9)" }}
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Creating Account..."
          >
            Sign Up Free
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AnonymousSignupModal;

